import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import groupBy from 'lodash/groupBy';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import {
  AccountHealth,
  Banner,
  ButtonExportCSVAsync,
  CardContainer,
  DateRangePicker,
  EmployeeLatestPayout,
  EmployeePayoutChart,
  EmployeeYearlyStats,
  Empty,
  FieldWrapper,
  Filter,
  FilterButton,
  getExpirationStatus,
  Label,
  ModalFactory,
  PageHeader,
  PageTitle,
  PayoutGroupCard,
  Section,
  SectionHeader,
  Select,
} from 'components';
import InfiniteScroll from 'components/InfiniteScroll/InfiniteScroll';
import EmployerSiteSelector from 'components/SiteSelector/EmployerSiteSelector';
import { appConfig } from 'config/app';
import { DDOption } from 'config/const';
import { Direction, theme, toRem } from 'design';
import { getQueryStringValue, useDebounce, useQueryString } from 'hooks';
import { InviteService } from 'lib';
import { UserInvites } from 'modals';
import ExpiredPayments from 'modals/ExpiredPayments';
import PendingPayments from 'modals/PendingPayments';
import { routeConstants } from 'routes';
import { selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Alerts, getUserAlertsRequest, getUserPayoutsRequest, Payouts, selectPayouts, selectPayoutsOffset, selectTotalCountPayouts, selectUserAlert } from 'store/Collections';
import { Employers } from 'store/Employers';
import { clearInvite, Invites } from 'store/Invites';
import { getUserPayoutStatsByDateRequest, selectPayoutStatsByDate, Stats } from 'store/Stats';
import { IStore } from 'store/types';
import { acceptAllUserInvitesRequest, getCurrentUserCardRequest, getUserInvitesRequest, selectCurrentUser, User } from 'store/User';
import { selectUserInvites } from 'store/User/selectors';
import { AlertType, ApiQueryOperator, IAlert, IApiQueryParam, IEmployerSite, InviteStatus, IPayout, IPayoutGroup, PayoutStatus } from 'types';
import { sortPayoutsByDisbursementMethod } from 'utils';
import { CSVFormatter, IExportCSV } from 'utils/csvformatter';
import { formatDateForApi } from 'utils/date';
import { capitalize, toTitleCase } from 'utils/strings';
import * as API from '../../api';

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  gap: ${toRem(theme.space.lg)};
  padding-top: ${toRem(theme.space.xl)};

  @media (min-width: ${theme.breakpoint.med}px) {
    padding-top: 0;
  }

  .payouts-section-header,
  .filter-section {
    margin-bottom: 0;
  }

  .date-range-label {
    display: none;

    @media (min-width: ${theme.breakpoint.med}px) {
      display: block;
    }
  }
`;

const BannerLink = styled(Link)`
  color: ${theme.color.danger[25]};
`;

const PayoutDateRange = styled.div`
  display: flex;
  align-items: center;

  label.date-range-label {
    margin-right: ${toRem(theme.space.sm)};
  }
`;

const PayoutGroupList = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.md)};
  width: 100%;
  list-style: none;
`;

const EmployeeStatsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.md)};
  width: 100%;
`;

const DEFAULT_END_DATE = dayjs();
const DEFAULT_START_DATE = dayjs().subtract(6, 'day');
const PAYOUT_LIST_LIMIT = 20;

const statusOptions = [
  {
    label: capitalize(PayoutStatus.PAID),
    value: PayoutStatus.PAID,
  },
  {
    label: capitalize(PayoutStatus.PAYROLL),
    value: PayoutStatus.PAYROLL,
  },
  {
    label: capitalize(PayoutStatus.CASH),
    value: PayoutStatus.CASH,
  },
  {
    label: capitalize(PayoutStatus.PENDING),
    value: PayoutStatus.PENDING,
  },
  {
    label: capitalize(PayoutStatus.CANCELED),
    value: PayoutStatus.CANCELED,
  },
  {
    label: capitalize(PayoutStatus.EXPIRED),
    value: PayoutStatus.EXPIRED,
  },
];

const getBannerContent = (userAlert: IAlert) => {
  const getBannerMessage = () => `${toTitleCase(userAlert.label)} - ${userAlert.message}`;

  switch (userAlert.type) {
    case AlertType.CARD:
      return <Link to={routeConstants.private.settingsPaymentMethod}>{getBannerMessage()}</Link>;
    case AlertType.CONTACT:
      return (
        <a href={appConfig.LINKS.SUPPORT} target="_blank" rel="noopener noreferrer">
          {userAlert.message}
        </a>
      );
    default:
      return getBannerMessage();
  }
};

const createPayoutGroups = (payouts: IPayout[]) => {
  const groupPayoutsByPaymentId = groupBy(payouts, 'paymentId');
  return Object.values(groupPayoutsByPaymentId).map((group) => ({
    id: group[0].paymentId,
    paymentDate: group[0].createdAt,
    site: group[0].site,
    notes: group[0].notes,
    payouts: sortPayoutsByDisbursementMethod(group),
  }));
};

const EmployeeDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { cardDetail, firstName, lastName, card } = useSelector(selectCurrentUser());
  const userAlert = useSelector(selectUserAlert());
  const payoutStatsByDate = useSelector(selectPayoutStatsByDate()) ?? [];

  const payouts = useSelector(selectPayouts());
  const payoutsTotal = useSelector(selectTotalCountPayouts());
  const payoutsOffset = useSelector(selectPayoutsOffset()) || 0;
  const nextPayoutsOffset = payoutsOffset + PAYOUT_LIST_LIMIT;

  const userInvites = useSelector(selectUserInvites);
  const userInvitesAccepted = userInvites?.every((invite) => invite.status === InviteStatus.ACCEPTED);
  const invitesIgnored = InviteService.getInviteIgnored();
  const inviteToken = InviteService.getInviteToken();

  const isPayoutStatsByDateLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_USER_PAYOUT_STATS_BY_DATE_REQUEST));
  const isPayoutsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.GET_USER_PAYOUTS_REQUEST));
  const isFetchingUserInvites = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.GET_USER_INVITES_REQUEST));
  const isAcceptingInvite = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Invites.ACCEPT_INVITE_REQUEST));
  const isAcceptingUserInvites = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.ACCEPT_ALL_USER_INVITES_REQUEST));
  const isEmployersLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employers.GET_USER_DASHBOARD_EMPLOYERS_REQUEST));
  const isAlertLoading = useSelector((state: IStore) => selectAPIRequestStatus(state, Alerts.GET_USER_ALERTS_REQUEST));

  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [loadingMorePayouts, setLoadingMorePayouts] = useState(false);
  const [payoutGroups, setPayoutGroups] = useState<IPayoutGroup[]>([]);

  const [urlQuery, setUrlQuery] = useQueryString('employeeDashboardPayouts', window.location, {
    limit: PAYOUT_LIST_LIMIT,
    q: [
      { key: 'created_at', operator: ApiQueryOperator.gte, value: formatDateForApi(DEFAULT_START_DATE) },
      { key: 'created_at', operator: ApiQueryOperator.lte, value: formatDateForApi(DEFAULT_END_DATE.add(1, 'day')) },
    ],
  });
  const siteIdFromUrl = getQueryStringValue(urlQuery.q, 'site_id', ApiQueryOperator.eq) as string;
  const startDate = getQueryStringValue(urlQuery.q, 'created_at', ApiQueryOperator.gte) as string;
  const endDate = formatDateForApi(dayjs(getQueryStringValue(urlQuery.q, 'created_at', ApiQueryOperator.lte) as string).subtract(1, 'day'));
  const statusesFromUrl = (urlQuery.q || []).filter((item: IApiQueryParam) => item.key === 'status').map((qStatus: any) => qStatus.value);
  const qForFilters = (urlQuery.q || []).filter((item: IApiQueryParam) => item.key !== 'created_at' && item.key !== 'site_id');
  const payoutsMissing = nextPayoutsOffset < payoutsTotal || loadingMorePayouts;

  useEffect(() => {
    if (inviteToken !== null) dispatch(clearInvite());
  }, [inviteToken]);

  useEffect(() => {
    if (card?.id && (!cardDetail || cardDetail.id !== card.id)) {
      dispatch(getCurrentUserCardRequest(card.id));
    }
  }, [dispatch, card, cardDetail]);

  useEffect(() => {
    if (!isAlertLoading) dispatch(getUserAlertsRequest());
  }, [dispatch, isAlertLoading]);

  useEffect(() => {
    if (!invitesIgnored && !userInvites && !isFetchingUserInvites) dispatch(getUserInvitesRequest());
  }, [invitesIgnored, isFetchingUserInvites]);

  useEffect(() => {
    if (!invitesIgnored && !!userInvites?.length && !userInvitesAccepted) showInviteModal();
  }, [invitesIgnored, userInvites, userInvitesAccepted]);

  useEffect(() => {
    fetchRecentPayouts(urlQuery);
  }, [urlQuery]);

  const getPayoutStatsByDate = useCallback(({ q }: IApiQueryOptions) => {
    const startDate = (getQueryStringValue(q, 'created_at', ApiQueryOperator.gte) as string) || formatDateForApi(DEFAULT_START_DATE);
    const endDateQuery = getQueryStringValue(q, 'created_at', ApiQueryOperator.lte) as string;
    const endDate = formatDateForApi(endDateQuery ? dayjs(endDateQuery).subtract(1, 'day') : DEFAULT_END_DATE);
    const qWithoutDates = (q || []).filter((item: IApiQueryParam) => item.key !== 'created_at');
    dispatch(getUserPayoutStatsByDateRequest(startDate, endDate, 'created_at', { q: qWithoutDates }));
  }, []);

  const fetchRecentPayouts = ({ sort, offset, limit, q }: IApiQueryOptions, skipStats?: boolean) => {
    setLoadingMorePayouts(true);
    dispatch(getUserPayoutsRequest({ limit, offset, sort, q }));
    if (!skipStats) getPayoutStatsByDate({ q });
  };

  const handleFilterChange = useCallback(
    (newFilters: IApiQueryParam[], keysToRemove: string[]) => {
      setPayoutGroups([]); // clear groups when filters change
      setUrlQuery({ ...urlQuery, q: [...(urlQuery.q || []).filter((param: IApiQueryParam) => !keysToRemove.includes(param.key)), ...newFilters] });
    },
    [urlQuery]
  );

  const filterPayoutDates = useCallback(
    (dates: string[]) => {
      handleFilterChange(
        [
          { key: 'created_at', operator: ApiQueryOperator.gte, value: formatDateForApi(dates[0]) },
          { key: 'created_at', operator: ApiQueryOperator.lte, value: formatDateForApi(dayjs(dates[1]).add(1, 'day')) },
        ],
        ['created_at']
      );
    },
    [handleFilterChange]
  );

  const onEmployerSearchChange = useCallback(
    (site?: IEmployerSite) => {
      handleFilterChange(site ? [{ key: 'site_id', operator: ApiQueryOperator.eq, value: site.id }] : [], ['site_id']);
    },
    [handleFilterChange]
  );

  const onStatusSearchChange = (newStatusSearchOption: DDOption[]) => {
    const mappedStatus = newStatusSearchOption.map(({ value }: DDOption) => value);
    handleFilterChange(mappedStatus.length > 0 ? [{ key: 'status', operator: ApiQueryOperator.in, value: mappedStatus.join(',') }] : [], ['status']);
  };
  const debouncedStatusChange = useDebounce(onStatusSearchChange, 500);

  useEffect(() => {
    if (loadingMorePayouts && payouts) {
      setLoadingMorePayouts(false);

      if (payouts.length === 0) return;

      const lastPayoutGroup = payoutGroups[payoutGroups.length - 1];

      // Create new payout groups from the next batch of payouts.
      // Need to include payouts from the last payment group in case it was incomplete
      const newPayoutGroups = createPayoutGroups([...(lastPayoutGroup?.payouts || []), ...payouts]);

      setPayoutGroups([...payoutGroups.slice(0, -1), ...newPayoutGroups]);
    }
  }, [payouts]);

  const loadMorePayouts = useCallback(
    async (query: IApiQueryOptions) => {
      if (nextPayoutsOffset < payoutsTotal) {
        // If there are more payouts to fetch, need to increase offset to fetch the next page of payouts
        fetchRecentPayouts({ ...query, offset: nextPayoutsOffset }, true);
      }
    },
    [payoutsTotal, nextPayoutsOffset]
  );

  const isExpired = cardDetail?.expDate ? getExpirationStatus(cardDetail?.expDate) === 'Expired' : false;

  const exportCSV = async (q?: IApiQueryParam[], sort?: string): Promise<IExportCSV> => {
    const response = await API.Users.listPayouts({ q, sort, offset: 0, limit: 0 });
    return CSVFormatter.userPayouts(response.body?.data);
  };

  const onAcceptInviteSuccess = () => history.replace(routeConstants.private.acceptInvitation);
  const handleAcceptInvite = useCallback(() => dispatch(acceptAllUserInvitesRequest(userInvites, onAcceptInviteSuccess)), [userInvites]);

  const handleIgnoreInvite = () => {
    InviteService.setInviteIgnored(true);
    hideInviteModal();
  };

  const [showInviteModal, hideInviteModal] = useModal(
    () => (
      <ModalFactory customModal isActive closeOnOutSideClick={false} onClose={handleIgnoreInvite}>
        <UserInvites isActive userInvites={userInvites} handleAcceptInvite={handleAcceptInvite} handleIgnoreInvite={handleIgnoreInvite} onClose={handleIgnoreInvite} isLoading={isAcceptingUserInvites || isAcceptingInvite} />
      </ModalFactory>
    ),
    [userInvites, isAcceptingUserInvites, isAcceptingInvite]
  );

  const [showPendingPaymentsModal, hidePendingPaymentsModal] = useModal(
    () => (
      <ModalFactory customModal isActive closeOnOutSideClick={false} onClose={hidePendingPaymentsModal}>
        <PendingPayments isActive onClose={hidePendingPaymentsModal} siteId={siteIdFromUrl} />
      </ModalFactory>
    ),
    [siteIdFromUrl]
  );

  const [showExpiredPaymentsModal, hideExpiredPaymentsModal] = useModal(
    () => (
      <ModalFactory customModal isActive closeOnOutSideClick={false} onClose={hideExpiredPaymentsModal}>
        <ExpiredPayments isActive onClose={hideExpiredPaymentsModal} siteId={siteIdFromUrl} />
      </ModalFactory>
    ),
    [siteIdFromUrl]
  );

  const onHelpAction = useCallback(
    (status: PayoutStatus) => {
      if (status === PayoutStatus.PENDING) {
        showPendingPaymentsModal();
      } else if (status === PayoutStatus.EXPIRED) {
        showExpiredPaymentsModal();
      }
    },
    [showPendingPaymentsModal, showExpiredPaymentsModal]
  );

  const renderPayoutChart = useMemo(() => (!payouts?.length ? null : <EmployeePayoutChart payoutStatsByDate={payoutStatsByDate} isLoading={isPayoutStatsByDateLoading} />), [payoutStatsByDate, payouts, isPayoutStatsByDateLoading]);

  // Scroll to top of page when the page is loaded and the user is on the employee dashboard page
  // The top is -120 to account for the fixed header of the page.
  useEffect(() => {
    window.scrollTo({ top: -120, left: 0 });
  }, [location.pathname, isEmployersLoading, isAlertLoading, userAlert, isExpired]);

  return (
    <>
      {userAlert && (
        <Banner appearance="caution" data-cy="alert-banner">
          {getBannerContent(userAlert)}
        </Banner>
      )}

      {isExpired && (
        <Banner appearance="danger" data-cy="expired-banner">
          <span>
            {'Your payment method has expired. '}
            <BannerLink to={routeConstants.private.settingsPaymentMethod}>Update or add a new one</BannerLink>
            {' to continue to receive payments.'}
          </span>
        </Banner>
      )}

      <PageHeader data-cy="page-header" hiddenMobile>
        <PageTitle label="Dashboard" title={`Welcome back, ${firstName}`} />
      </PageHeader>

      <Section afterHeaderCardRow>
        <Layout>
          <EmployerSiteSelector onChange={onEmployerSearchChange} selectedSite={siteIdFromUrl} />

          <EmployeeStatsContainer>
            <CardContainer iconType="PieChart" title="Summary" direction={Direction.ROW} dataCy="employee-summary-stats">
              <EmployeeLatestPayout siteId={siteIdFromUrl} />
              <EmployeeYearlyStats siteId={siteIdFromUrl} />
            </CardContainer>
            <AccountHealth siteId={siteIdFromUrl} onHelpAction={onHelpAction} />
          </EmployeeStatsContainer>

          <SectionHeader title="Payments" value={`(${payoutsTotal})`} isLoading={isPayoutsLoading} className="payouts-section-header" data-cy="payout-total-header">
            <PayoutDateRange>
              <Label className="date-range-label">Date Range</Label>
              <DateRangePicker
                className="payout-date-range-picker"
                data-cy="payout-date-range-picker"
                fluidWidth={false}
                inputHeight="small"
                onSubmit={filterPayoutDates}
                pickerPosition="right"
                startDate={startDate}
                endDate={endDate}
                submitLabel="apply"
                setGlobalDateRange={false}
              />
            </PayoutDateRange>
            <FilterButton filterActive={filterActive} appliedFilters={qForFilters.length} onClick={() => setFilterActive(!filterActive)} />
            <ButtonExportCSVAsync data-cy="export-btn" disabled={isPayoutsLoading || !payouts?.length} exportFunc={() => exportCSV(urlQuery.q, urlQuery.sort)} fileName={`Payments_${firstName} ${lastName}_${startDate}_${endDate}`} />
          </SectionHeader>

          <Filter isVisible={filterActive} className="filter-section" data-cy="filter-section">
            <FieldWrapper label="Status">
              <Select options={statusOptions} values={statusOptions.filter((optionVal: { label: string; value: string }) => statusesFromUrl.includes(optionVal.value))} multi onChange={debouncedStatusChange} data-cy="status-select" />
            </FieldWrapper>
          </Filter>

          {renderPayoutChart}

          <PayoutGroupList>
            {!payoutGroups.length ? (
              loadingMorePayouts ? (
                <Skeleton width="100%" height={toRem(100)} />
              ) : (
                <Empty container containerHeight={250} icon="Payment" message="No payments found" />
              )
            ) : (
              <InfiniteScroll fetchMore={() => loadMorePayouts(urlQuery)} earlyFetch>
                {payoutGroups.map((payoutGroup, index) => {
                  const isLastGroup = index === payoutGroups.length - 1;
                  return <PayoutGroupCard key={payoutGroup.id} payoutGroup={payoutGroup} isLoading={isLastGroup && payoutsMissing} />;
                })}
              </InfiniteScroll>
            )}
          </PayoutGroupList>
        </Layout>
      </Section>
    </>
  );
};

export default EmployeeDashboard;
